export interface TourRect {
  top: number
  left: number
  width: number
  height: number
}

export interface TourViewport {
  width: number
  height: number
}

export type TourPlacement = "top" | "bottom" | "left" | "right"

const DEFAULT_TOOLTIP_SIZE = { width: 380, height: 220 }
const GAP = 16
const VIEWPORT_MARGIN = 16

/** The spotlight cutout — the target rect plus breathing room. Padding is
 * clamped to the 6–10px range the visual spec calls for. */
export const computeSpotlightRect = (
  target: TourRect,
  padding = 8
): TourRect => {
  const p = Math.min(10, Math.max(6, padding))
  return {
    top: target.top - p,
    left: target.left - p,
    width: target.width + p * 2,
    height: target.height + p * 2
  }
}

/**
 * Picks a tooltip placement that actually fits the viewport around the
 * target, trying the caller's preferred side first and falling back to
 * whichever side has the most room — the tooltip must never cover the
 * target when another placement is available. Position is always clamped
 * inside the viewport (minus a margin) so even a tight fallback never
 * slides off-screen.
 */
export const computeTooltipPlacement = (
  target: TourRect,
  viewport: TourViewport,
  preferred: TourPlacement = "bottom",
  tooltipSize: { width: number; height: number } = DEFAULT_TOOLTIP_SIZE
): { placement: TourPlacement; top: number; left: number } => {
  const space: Record<TourPlacement, number> = {
    top: target.top,
    bottom: viewport.height - (target.top + target.height),
    left: target.left,
    right: viewport.width - (target.left + target.width)
  }

  const fits = (p: TourPlacement): boolean =>
    p === "top" || p === "bottom"
      ? space[p] >= tooltipSize.height + GAP
      : space[p] >= tooltipSize.width + GAP

  const bySpaceDesc = (["top", "bottom", "left", "right"] as TourPlacement[])
    .filter((p) => p !== preferred)
    .sort((a, b) => space[b] - space[a])
  const order: TourPlacement[] = [preferred, ...bySpaceDesc]

  const placement = order.find(fits) ?? order[0]

  let top: number
  let left: number

  if (placement === "top" || placement === "bottom") {
    top =
      placement === "top"
        ? target.top - tooltipSize.height - GAP
        : target.top + target.height + GAP
    left = target.left + target.width / 2 - tooltipSize.width / 2
  } else {
    left =
      placement === "left"
        ? target.left - tooltipSize.width - GAP
        : target.left + target.width + GAP
    top = target.top + target.height / 2 - tooltipSize.height / 2
  }

  left = clamp(
    left,
    VIEWPORT_MARGIN,
    viewport.width - tooltipSize.width - VIEWPORT_MARGIN
  )
  top = clamp(
    top,
    VIEWPORT_MARGIN,
    viewport.height - tooltipSize.height - VIEWPORT_MARGIN
  )

  return { placement, top, left }
}

const clamp = (value: number, min: number, max: number): number =>
  // A viewport smaller than the tooltip itself makes min > max — hold the
  // margin rather than let the tooltip invert off both edges at once.
  max < min ? min : Math.min(Math.max(value, min), max)

/** True once the target rect is at least mostly inside the viewport — the
 * signal that a smooth-scroll before showing the tooltip is unnecessary. */
export const isRectInViewport = (
  rect: TourRect,
  viewport: TourViewport,
  stickyHeaderHeight = 0
): boolean =>
  rect.top >= stickyHeaderHeight &&
  rect.left >= 0 &&
  rect.top + rect.height <= viewport.height &&
  rect.left + rect.width <= viewport.width
